import { useState } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../services/authService";
import { useSeller } from "../context/SellerContext";
import { useToast } from "../context/ToastContext";
import { formatPhoneNumber } from "../utils/format";

function useSignupForm() {
  const navigate = useNavigate();
  const { seller, isSellerSite } = useSeller();
  const { showToast } = useToast();
  const [memberType, setMemberType] = useState("personal");
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    passwordConfirm: "",
    name: "",
    email: "",
    phone: "",
    mobile: "",
    zipcode: "",
    address: "",
    addressDetail: "",
    gender: "",
    businessInfo: {
      businessNumber: "",
      companyName: "",
      ceoName: "",
      businessType: "",
      businessCategory: "",
      businessAddress: "",
      businessPhone: "",
      taxInvoiceEmail: "",
    },
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [emailChecked, setEmailChecked] = useState(false);
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [mobileChecked, setMobileChecked] = useState(false);

  const [agreements, setAgreements] = useState({
    all: false,
    terms: false,
    privacy: false,
    thirdParty: false,
    ageVerified: false,
    marketing: false,
  });

  const [activeModal, setActiveModal] = useState(null);

  const handleOpenModal = (type) => setActiveModal(type);
  const handleCloseModal = () => setActiveModal(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("businessInfo.")) {
      const fieldName = name.split(".")[1];
      setFormData({
        ...formData,
        businessInfo: { ...formData.businessInfo, [fieldName]: value },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    if (name === "email") setEmailChecked(false);
    if (name === "username") setUsernameChecked(false);

    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: "" });
    }
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatPhoneNumber(value);

    if (name.startsWith("businessInfo.")) {
      const fieldName = name.split(".")[1];
      setFormData({
        ...formData,
        businessInfo: { ...formData.businessInfo, [fieldName]: formatted },
      });
    } else {
      setFormData({ ...formData, [name]: formatted });
    }

    // 휴대폰 번호 변경 시 중복 체크 결과 초기화
    if (name === "mobile") setMobileChecked(false);
  };

  const checkUsernameDuplicate = async () => {
    if (!formData.username) {
      showToast("아이디를 입력해주세요.", "info");
      return;
    }
    if (formData.username.length < 4 || formData.username.length > 20) {
      showToast("아이디는 4-20자여야 합니다.", "info");
      return;
    }
    try {
      const response = await authService.checkUsernameDuplicate(formData.username, seller?.id);
      if (response.available) {
        setUsernameChecked(true);
        showToast("사용 가능한 아이디입니다.", "success");
      }
    } catch (error) {
      setUsernameChecked(false);
      const msg = error.response?.data?.message || "아이디 확인 중 오류가 발생했습니다.";
      showToast(msg, "error");
    }
  };

  const checkEmailDuplicate = async () => {
    if (!formData.email) {
      showToast("이메일을 입력해주세요.", "info");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      showToast("올바른 이메일 형식이 아닙니다.", "info");
      return;
    }
    try {
      const response = await authService.checkEmailDuplicate(formData.email, seller?.id);
      if (response.available) {
        setEmailChecked(true);
        showToast("사용 가능한 이메일입니다.", "success");
      }
    } catch (error) {
      setEmailChecked(false);
      const msg = error.response?.data?.message || "이메일 확인 중 오류가 발생했습니다.";
      showToast(msg, "error");
    }
  };

  const checkMobileDuplicate = async () => {
    if (!formData.mobile) {
      showToast("휴대폰 번호를 입력해주세요.", "info");
      return;
    }
    if (!/^\d{3}-\d{4}-\d{4}$/.test(formData.mobile)) {
      showToast("올바른 휴대폰 번호 형식이 아닙니다 (010-0000-0000).", "info");
      return;
    }
    try {
      const response = await authService.checkMobileDuplicate(formData.mobile, seller?.id);
      if (response.available) {
        setMobileChecked(true);
        showToast("사용 가능한 휴대폰 번호입니다.", "success");
      }
    } catch (error) {
      setMobileChecked(false);
      const msg = error.response?.data?.message || "휴대폰 번호 확인 중 오류가 발생했습니다.";
      showToast(msg, "error");
    }
  };

  const openAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: function (data) {
        setFormData({ ...formData, zipcode: data.zonecode, address: data.address });
      },
    }).open();
  };

  const handleAllAgreement = (e) => {
    const checked = e.target.checked;
    setAgreements({
      all: checked,
      terms: checked,
      privacy: checked,
      thirdParty: checked,
      ageVerified: checked,
      marketing: checked,
    });
  };

  const handleAgreement = (e) => {
    const { name, checked } = e.target;
    const newAgreements = { ...agreements, [name]: checked };
    newAgreements.all =
      newAgreements.terms &&
      newAgreements.privacy &&
      newAgreements.thirdParty &&
      newAgreements.ageVerified &&
      newAgreements.marketing;
    setAgreements(newAgreements);
  };

  const validateForm = () => {
    const errors = {};

    if (!agreements.terms) errors.terms = "이용약관에 동의해주세요";
    if (!agreements.privacy) errors.privacy = "개인정보 처리방침에 동의해주세요";
    if (!agreements.thirdParty) errors.thirdParty = "개인정보 제3자 제공에 동의해주세요";
    if (!agreements.ageVerified) errors.ageVerified = "만 14세 이상임에 동의해주세요";

    if (!formData.username) {
      errors.username = "아이디는 필수입니다";
    } else if (formData.username.length < 4 || formData.username.length > 20) {
      errors.username = "아이디는 4-20자여야 합니다";
    } else if (!usernameChecked) {
      errors.username = "아이디 중복확인을 해주세요";
    }

    if (!formData.password) {
      errors.password = "비밀번호는 필수입니다";
    } else if (formData.password.length < 6) {
      errors.password = "비밀번호는 최소 6자 이상이어야 합니다";
    }

    if (!formData.passwordConfirm) {
      errors.passwordConfirm = "비밀번호 확인은 필수입니다";
    } else if (formData.password !== formData.passwordConfirm) {
      errors.passwordConfirm = "비밀번호가 일치하지 않습니다";
    }

    if (!formData.name) errors.name = "이름은 필수입니다";

    if (!formData.mobile) {
      errors.mobile = "휴대폰번호는 필수입니다";
    } else if (!/^\d{3}-\d{4}-\d{4}$/.test(formData.mobile)) {
      errors.mobile = "올바른 휴대폰번호 형식이 아닙니다 (000-0000-0000)";
    } else if (!mobileChecked) {
      errors.mobile = "휴대폰 번호 중복확인을 진행해주세요";
    }

    if (!formData.email) {
      errors.email = "이메일은 필수입니다";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "올바른 이메일 형식이 아닙니다";
    } else if (!emailChecked) {
      errors.email = "이메일 중복확인을 해주세요";
    }

    if (memberType === "business") {
      if (!formData.businessInfo.businessNumber) errors.businessNumber = "사업자등록번호는 필수입니다";
      if (!formData.businessInfo.companyName) errors.companyName = "회사명은 필수입니다";
      if (!formData.businessInfo.ceoName) errors.ceoName = "대표자명은 필수입니다";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { passwordConfirm, ...signupData } = formData;
      const submitData = {
        ...signupData,
        memberType,
        termsAgreed: agreements.terms,
        privacyAgreed: agreements.privacy,
        thirdPartyAgreed: agreements.thirdParty,
        ageVerified: agreements.ageVerified,
        marketingAgreed: agreements.marketing,
      };

      if (isSellerSite && seller) {
        submitData.sellerId = seller.id;
      }
      await authService.signup(submitData);
      showToast("회원가입이 완료되었습니다! 로그인해주세요.", "success");
      navigate("/login");
    } catch (err) {
      const msg = err.response?.data?.message || "회원가입에 실패했습니다. 다시 시도해주세요.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 소셜 로그인: 서브몰에서 메인 도메인 OAuth 허브로 리다이렉트
   * 메인 허브에서 카카오/네이버 인가 → 서브몰로 복귀
   */
  const redirectToOAuthHub = (provider) => {
    const mainHub = process.env.REACT_APP_MAIN_HUB_URL || window.location.origin;
    const returnTo = encodeURIComponent(window.location.origin);
    window.location.href = `${mainHub}/auth/${provider}?returnTo=${returnTo}`;
  };

  const handleKakaoSignup = () => redirectToOAuthHub('kakao');
  const handleNaverSignup = () => redirectToOAuthHub('naver');

  return {
    seller,
    isSellerSite,
    memberType,
    setMemberType,
    formData,
    error,
    loading,
    validationErrors,
    emailChecked,
    usernameChecked,
    mobileChecked,
    agreements,
    activeModal,
    handleOpenModal,
    handleCloseModal,
    handleChange,
    handlePhoneChange,
    checkUsernameDuplicate,
    checkEmailDuplicate,
    checkMobileDuplicate,
    openAddressSearch,
    handleAllAgreement,
    handleAgreement,
    handleSubmit,
    handleKakaoSignup,
    handleNaverSignup,
  };
}

export default useSignupForm;
